import { ChevronLeftIcon } from '@approvalmax/ui';
import { Button, Grid } from '@approvalmax/ui/src/components';
import { memo, useMemo } from 'react';
import { useFormState } from 'react-hook-form';
import { routingService } from 'services/routing';
import { TermsAndPolicy } from 'shared/components/(wallets)/kyc/TermsAndPolicy';
import { useSubmitKyc } from 'shared/data/walletsKyc/v1';
import { useCompanyIdBasedOnUrl } from 'shared/hooks';
import { getPath, Path } from 'urlBuilder';

import { useIsSubmitAvailable } from '../../hooks/useIsSubmitAvailable';
import { useWizardNavigation } from '../../hooks/useWizardNavigation';
import { messages } from './Controls.messages';

export const Controls = memo(() => {
    const { isFirstStep, isLastStep, navigateBack } = useWizardNavigation();
    const { companyId } = useCompanyIdBasedOnUrl();
    const formState = useFormState();
    const { isSubmitAvailable } = useIsSubmitAvailable();
    const { mutate, isLoading } = useSubmitKyc({
        onSuccess: () => {
            routingService.push(getPath(Path.walletsKycSubmitted, companyId));
        },
    });

    const nextButtonLabel = useMemo(() => {
        if (isFirstStep) {
            return messages.getStarted;
        }

        if (isLastStep) {
            return messages.next;
        }

        return messages.next;
    }, [isFirstStep, isLastStep]);

    const handleSubmit = () => {
        mutate({ params: { path: { organizationId: companyId } } });
    };

    return (
        <Grid gap={24}>
            <Grid
                gridAutoColumns={isFirstStep ? '1fr' : 'max-content 172px'}
                justifyContent='flex-end'
                gridAutoFlow='column'
                gap={16}
            >
                {!isFirstStep && (
                    <Button
                        color='midnight40'
                        data-qa='kyc-move-back'
                        startIcon={<ChevronLeftIcon />}
                        onClick={navigateBack}
                        variant='outline'
                    >
                        {messages.back}
                    </Button>
                )}

                {isLastStep ? (
                    <Button
                        color='blue80'
                        disabled={!isSubmitAvailable}
                        progress={isLoading}
                        data-qa='kyc-submit'
                        onClick={handleSubmit}
                    >
                        {messages.submit}
                    </Button>
                ) : (
                    <Button
                        color='blue80'
                        size={isFirstStep ? 'large' : undefined}
                        disabled={formState.disabled}
                        progress={formState.isSubmitting}
                        type='submit'
                        data-qa='kyc-move-forward'
                    >
                        {nextButtonLabel}
                    </Button>
                )}
            </Grid>

            {isLastStep && <TermsAndPolicy actionName={messages.getStarted} />}
        </Grid>
    );
});

Controls.displayName = 'Controls';
